import { useState, useRef, useEffect, useCallback, KeyboardEvent, ChangeEvent } from 'react';
import { useMentionUsers } from '../../api/notifications';
import type { MentionUser } from '../../types/notification';

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  required?: boolean;
}

interface DropdownPosition {
  top: number;
  left: number;
}

export function MentionInput({
  value,
  onChange,
  placeholder = 'Schreiben Sie eine Notiz... (@ um jemanden zu erwaehnen)',
  rows = 4,
  className = '',
  required = false,
}: MentionInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filteredUsers, setFilteredUsers] = useState<MentionUser[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0 });

  const { data: usersData } = useMentionUsers();
  const users = usersData?.data || [];

  // Filter users based on mention query
  useEffect(() => {
    if (!mentionQuery) {
      setFilteredUsers(users.slice(0, 5));
      return;
    }

    const query = mentionQuery.toLowerCase();
    const filtered = users.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const firstName = user.firstName.toLowerCase();
      return firstName.startsWith(query) || fullName.includes(query);
    });

    setFilteredUsers(filtered.slice(0, 5));
    setSelectedIndex(0);
  }, [mentionQuery, users]);

  // Calculate cursor position in pixels
  const getCursorPosition = useCallback((cursorIndex: number): DropdownPosition => {
    const textarea = textareaRef.current;
    const mirror = mirrorRef.current;

    if (!textarea || !mirror) {
      return { top: 0, left: 0 };
    }

    // Copy textarea styles to mirror div
    const style = window.getComputedStyle(textarea);
    const properties = [
      'fontFamily', 'fontSize', 'fontWeight', 'fontStyle',
      'letterSpacing', 'textTransform', 'wordSpacing',
      'textIndent', 'whiteSpace', 'wordWrap', 'wordBreak',
      'paddingLeft', 'paddingRight', 'paddingTop', 'paddingBottom',
      'borderLeftWidth', 'borderRightWidth', 'borderTopWidth', 'borderBottomWidth',
      'boxSizing', 'lineHeight'
    ];

    properties.forEach((prop) => {
      (mirror.style as any)[prop] = style.getPropertyValue(
        prop.replace(/([A-Z])/g, '-$1').toLowerCase()
      );
    });

    mirror.style.width = `${textarea.clientWidth}px`;
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.whiteSpace = 'pre-wrap';
    mirror.style.overflowWrap = 'break-word';

    // Get text up to cursor and add a span to mark cursor position
    const textBeforeCursor = value.substring(0, cursorIndex);
    mirror.innerHTML = '';

    const textNode = document.createTextNode(textBeforeCursor);
    const cursorSpan = document.createElement('span');
    cursorSpan.id = 'cursor-marker';
    cursorSpan.textContent = '|';

    mirror.appendChild(textNode);
    mirror.appendChild(cursorSpan);

    const cursorMarker = mirror.querySelector('#cursor-marker');
    if (!cursorMarker) {
      return { top: 0, left: 0 };
    }

    const cursorRect = cursorMarker.getBoundingClientRect();
    const mirrorRect = mirror.getBoundingClientRect();

    // Calculate position relative to textarea
    const relativeTop = cursorRect.top - mirrorRect.top;
    const relativeLeft = cursorRect.left - mirrorRect.left;

    // Account for textarea scroll
    const scrollTop = textarea.scrollTop;

    return {
      top: relativeTop - scrollTop + parseInt(style.paddingTop) + 20, // 20px below cursor line
      left: Math.min(relativeLeft, textarea.clientWidth - 200), // Ensure dropdown fits
    };
  }, [value]);

  // Handle text change and detect @ mentions
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart;

      onChange(newValue);

      // Look for @ symbol before cursor
      const textBeforeCursor = newValue.slice(0, cursorPos);
      const atIndex = textBeforeCursor.lastIndexOf('@');

      if (atIndex !== -1) {
        const textAfterAt = textBeforeCursor.slice(atIndex + 1);
        // Check if there's a space before @ (or it's at start)
        const charBeforeAt = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';

        // Only trigger if @ is at start of word and no space after
        if ((charBeforeAt === ' ' || charBeforeAt === '\n' || atIndex === 0) && !textAfterAt.includes(' ')) {
          setShowSuggestions(true);
          setMentionQuery(textAfterAt);
          setMentionStart(atIndex);

          // Calculate and set dropdown position
          const position = getCursorPosition(cursorPos);
          setDropdownPosition(position);
          return;
        }
      }

      setShowSuggestions(false);
      setMentionQuery('');
      setMentionStart(-1);
    },
    [onChange, getCursorPosition],
  );

  // Insert selected mention
  const insertMention = useCallback(
    (user: MentionUser) => {
      if (mentionStart === -1 || !textareaRef.current) return;

      const cursorPos = textareaRef.current.selectionStart;
      const mentionText = `@${user.firstName} ${user.lastName}`;
      const newValue =
        value.slice(0, mentionStart) +
        mentionText +
        ' ' +
        value.slice(cursorPos);

      onChange(newValue);
      setShowSuggestions(false);
      setMentionQuery('');
      setMentionStart(-1);

      // Set cursor position after mention
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = mentionStart + mentionText.length + 1;
          textareaRef.current.selectionStart = newCursorPos;
          textareaRef.current.selectionEnd = newCursorPos;
          textareaRef.current.focus();
        }
      }, 0);
    },
    [value, onChange, mentionStart],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!showSuggestions || filteredUsers.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % filteredUsers.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev === 0 ? filteredUsers.length - 1 : prev - 1,
          );
          break;
        case 'Enter':
          if (showSuggestions) {
            e.preventDefault();
            insertMention(filteredUsers[selectedIndex]);
          }
          break;
        case 'Escape':
          setShowSuggestions(false);
          break;
        case 'Tab':
          if (showSuggestions) {
            e.preventDefault();
            insertMention(filteredUsers[selectedIndex]);
          }
          break;
      }
    },
    [showSuggestions, filteredUsers, selectedIndex, insertMention],
  );

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative">
      {/* Hidden mirror div for cursor position calculation */}
      <div
        ref={mirrorRef}
        aria-hidden="true"
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          top: 0,
          left: 0,
        }}
      />

      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        required={required}
        className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm ${className}`}
      />

      {/* Suggestions dropdown positioned near cursor */}
      {showSuggestions && filteredUsers.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          <ul className="py-1">
            {filteredUsers.map((user, index) => (
              <li
                key={user.id}
                onClick={() => insertMention(user)}
                className={`px-3 py-2 text-sm cursor-pointer ${
                  index === selectedIndex
                    ? 'bg-primary-100 text-primary-900'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span className="font-medium">
                  {user.firstName} {user.lastName}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
